<script context="module">
  function isolateLastWordForWrapping(text = "") {
    text = text.trim();
    let lastWord;
    let reducedText = "";
    text.split(" ").forEach((word, index, array) => {
      if (index !== array.length - 1) {
        reducedText += `${word} `;
      } else {
        lastWord = word;
      }
    });
    return { reducedText, lastWord };
  }
</script>

<script>
  // @ts-check
  import Icon from "../../atoms/Icon.svelte";
  import Image from "../../atoms/Image.svelte";
  import Tags from "../../atoms/Tags.svelte";
  import { validatePropObjectWithSchema } from "../../utilities/logs";

  // An Object type Link with URL, and name
  /** @typedef {Object} Link
   * @property {string} url
   * @property {import("../../atoms/Tags.svelte").Tag[]} [tags]
   * @property {string} headline
   * @property {string} subCopy
   * @property {string | null} imageSrc
   * @property {string | null} srcset
   * @property {string | null} sizes
   */

  /** @type {Link} */
  export let link;

  const linkSchema = {
    url: "string",
    tags: "array",
    imageSrc: "string",
    srcset: "string",
    sizes: "string"
  };

  validatePropObjectWithSchema({
    componentName: "ResultListItemLink",
    propName: "link",
    propValue: link,
    schema: linkSchema
  });

  /** The type of layout variant to render
   * @type { "default" | "small" }
   */
  export let layoutVariant = "default";

  export let containerClass = undefined;

  export let theme;

  $: ({ reducedText, lastWord } = isolateLastWordForWrapping(link.headline));

  const imageClass = {
    default: "w-[3.625rem] md:w-[5.625rem] lg:w-[6.75rem] xl:w-[8.25rem]",
    small: "w-[1.875rem] md:w-15 lg:w-[4.5rem] xl:w-[5.75rem] "
  };
</script>

<a
  href={link.url}
  class="group {containerClass} flex items-center transition {theme.textColor} focus-visible:outline-dashed focus-visible:outline-offset-4 {theme.focusOutlineColor}"
  data-sveltekit-reload
>
  <div class="w-full">
    <div class="flex">
      <div class="flex flex-1 flex-col justify-between gap-3 md:flex-auto lg:gap-4">
        {#if link.tags?.length > 0 && layoutVariant === "default"}
          <Tags
            tags={link.tags.slice(0, 2)}
            class="label gap-x-5 text-black-60"
            bulletClass="bullet"
          />
        {/if}
        <h3
          class="heading-xs inline items-center gap-2 pr-4 {`group-${theme.hoverTextColor} group-${theme.focusTextColor}`}"
        >
          {#if reducedText}
            <span>
              {reducedText}
            </span><!--The span below is purposely positioned as such to prevent adding an extra space to the view.-->
          {/if}<span class="whitespace-nowrap" class:single-word-wrap={!reducedText && lastWord}>
            <span>
              {lastWord}
            </span>
            <span
              class="opacity-transparent
            group-hover:opacity-opaque
            group-focus-visible:opacity-opaque
          "
            >
              <Icon name="arrowRight" iconClass="inline h-[24px] w-[24px]" inheritSize />
            </span>
          </span>
        </h3>
        <span class="hidden md:block">
          <p class="body-sm line-clamp-1">{link.subCopy}</p>
        </span>
      </div>

      {#if link.imageSrc}
        <div class="block md:hidden">
          <Image
            id="static-menu-link-image"
            aspectRatioClasses="aspect-1/1"
            containerClass="justify-self-end {imageClass[layoutVariant]}"
            src={link.imageSrc}
            srcset={link.srcset}
            sizes={link.sizes}
            alt=""
          />
        </div>
      {/if}

      <div class="hidden flex-1 items-center justify-end pl-[2.125rem] md:flex lg:pl-30 xl:pl-6">
        {#if link.imageSrc}
          <Image
            id="static-menu-link-image"
            aspectRatioClasses="aspect-1/1"
            containerClass={imageClass[layoutVariant]}
            src={link.imageSrc}
            srcset={link.srcset}
            sizes={link.sizes}
            alt=""
          />
        {/if}
      </div>
    </div>
    <span class="block md:hidden">
      <p class="body-sm mt-3 line-clamp-2">{link.subCopy}</p>
    </span>
  </div>
</a>

<!--This is a compromise until I can implement a better solution for NOT wrapping the text of an external
link that happens to be a really long word. The compromise is that I just won't apply the wrapping rules on
really small screens since it can cut off some of the text that refuses to wrap. - Christina -->
<style>
  @media (max-width: 400px) {
    .single-word-wrap {
      white-space: normal !important;
    }
  }
</style>
